var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useState } from 'react';
import { animated, useTransition } from '@react-spring/web';
import styled from 'styled-components';
import uniqby from 'lodash.uniqby';
import cx from 'classnames';
import { CloseO } from 'styled-cssgg';
import { TabsContext, defaultValue, useTabsContext, } from './tabs-context';
import { Box } from '../box/box';
const StyledTabs = styled.nav `
  background-color: ${() => 'var(--bg-color)'};
`;
const StyledTabPanel = styled.div ``;
export const TabsPanel = (props) => {
    const { onAdd, activeKey } = useTabsContext();
    useEffect(() => {
        onAdd(props.itemKey, props.label);
    }, [props.itemKey]);
    if (activeKey !== props.itemKey) {
        return null;
    }
    return React.createElement(StyledTabPanel, null, props.children);
};
const StyledTabItem = styled(animated.div) `
  overflow: hidden;
`;
const StyledTabItemContent = styled.div `
  &[data-tabtheme='button'] {
    color: ${(props) => props.isActive ? 'var(--text-color-0)' : 'var(--text-color-2)'};
    padding: 2px 12px;
    cursor: pointer;
    border-radius: 12px;
    background-color: ${(props) => props.isActive ? 'var(--active-bg-color)' : undefined};
  }
  &[data-tabtheme='tab'] {
    color: ${(props) => props.isActive ? 'var(--text-color-0)' : 'var(--text-color-2)'};
    padding: 4px 12px;
    cursor: pointer;
    background-color: ${(props) => props.isActive ? 'var(--active-bg-color)' : 'var(--bg-color)'};
    display: flex;
    justify-content: space-between;
    align-items: center;
    --ggs: 0.75;
    box-sizing: border-box;
    min-width: 140px;
  }
`;
const StyledTabList = styled(Box) `
  display: flex;
  height: 30px;
  border-radius: 0px;
  border-left: none;
  border-right: none;
  border: ${(props) => (props.borderless ? 'none' : undefined)};
  &[data-tabtheme='button'] {
    gap: 4px;
    border: none;
    box-shadow: none;
    width: min-content;
    [data-role='tab-item']:first-child {
      margin-left: 4px;
    }
  }
`;
export const InnerTabs = (props) => {
    const { tabs, activeKey, onChange, tabTheme, onDelete } = useTabsContext();
    const transitions = useTransition(tabs, {
        from: { opacity: 0, width: 0 },
        enter: { opacity: 1, width: 140 },
        leave: { opacity: 0, width: 0 },
        delay: 200,
    });
    const borderless = tabs.length === 0;
    return (React.createElement(StyledTabs, { className: props.className, "data-role": "tabs" },
        React.createElement(StyledTabList, { display: "flex", alignItems: "center", borderless: borderless, "data-tabtheme": tabTheme, className: "tab-list" }, transitions((styles, item) => {
            return (React.createElement(StyledTabItem, { style: Object.assign(Object.assign({}, styles), { width: tabTheme === 'button' ? undefined : styles.width }), key: item.itemKey },
                React.createElement(StyledTabItemContent, { tabTheme: tabTheme, "data-tabtheme": tabTheme, "data-role": "tab-item", className: "tab-item", onClick: () => {
                        onChange === null || onChange === void 0 ? void 0 : onChange(item.itemKey);
                    }, key: item.itemKey, isActive: activeKey === item.itemKey },
                    item.label,
                    tabTheme === 'tab' && (React.createElement(CloseO, { "data-role": "close", onClick: () => onDelete === null || onDelete === void 0 ? void 0 : onDelete(item.itemKey) })))));
        })),
        props.children));
};
export const useTabs = (_a) => {
    var _b;
    var { defaultTabs = [] } = _a, props = __rest(_a, ["defaultTabs"]);
    const [tabs, setTabs] = useState(defaultTabs);
    const [activeKey, setActiveKey] = useState(props.defaultActiveKey);
    const handleAdd = (itemKey, label) => {
        setTabs((prev) => prev.concat([
            {
                label,
                itemKey,
                deleted: false,
                disabled: false,
            },
        ]));
    };
    const handleChange = (itemKey) => {
        var _a;
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, itemKey);
        setActiveKey(itemKey);
    };
    const handleDelete = (itemKey) => {
        var _a;
        (_a = props.onDelete) === null || _a === void 0 ? void 0 : _a.call(props, itemKey);
        setTabs((prev) => {
            return prev.filter((item) => {
                if (item.itemKey === itemKey) {
                    return false;
                }
                return true;
            });
        });
    };
    const arrayOfTabs = useMemo(() => {
        if (props.tabs) {
            return uniqby(props.tabs, 'itemKey');
        }
        if (!tabs) {
            return [];
        }
        return uniqby(tabs, 'itemKey');
    }, [tabs, props.tabs]);
    return {
        tabs: arrayOfTabs,
        handleChange,
        handleDelete,
        activeKey: props.activeKey || activeKey || ((_b = arrayOfTabs[0]) === null || _b === void 0 ? void 0 : _b.itemKey),
        handleAdd,
    };
};
export const Tabs = (_a) => {
    var { tabTheme = 'tab' } = _a, props = __rest(_a, ["tabTheme"]);
    const { activeKey, tabs, handleAdd, handleChange, handleDelete } = useTabs(props);
    return (React.createElement(TabsContext.Provider, { value: Object.assign(Object.assign({}, defaultValue), { activeKey,
            tabs, onAdd: handleAdd, onChange: handleChange, onDelete: handleDelete, tabTheme }) },
        React.createElement(InnerTabs, { className: cx('tabs', props.className), defaultActiveKey: props.defaultActiveKey }, props.children)));
};
